import styled, { css, keyframes } from 'styled-components';

import { SearchIcon } from '~/assets';
import agentBackground from '~/assets/agentBackground.png';
import { Input } from '~/components';
import BottomSheet from '~/components/BottomSheet';
import Button from '~/components/Button';
import { headerHeight } from '~/components/layout/Header/Header.styles';
import { ThemeProp } from '~/theme/Themes';

const CHIP_PENDING_BG = '#ffe9db';

const BLOCKQUOTE_INFO_ICON = `url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='12' height='12' rx='6' fill='%230052CC'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M6.00037 5.25098C6.41456 5.251 6.75037 5.58678 6.75037 6.00098V8.74414C6.75009 9.1581 6.41439 9.49411 6.00037 9.49414C5.58632 9.49414 5.25065 9.15812 5.25037 8.74414V6.00098C5.25037 5.58676 5.58615 5.25098 6.00037 5.25098ZM6.0072 2.50781C6.42136 2.50784 6.75715 2.84366 6.7572 3.25781C6.7572 3.67201 6.42139 4.00779 6.0072 4.00781H6.00037C5.58615 4.00781 5.25037 3.67203 5.25037 3.25781C5.25042 2.84364 5.58618 2.50781 6.00037 2.50781H6.0072Z' fill='white'/%3E%3C/svg%3E")`;

// =============================================================================
// Mention chip — shared base styles (used in editor + read-only chat bubbles)
// =============================================================================

const mentionChipBaseStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
  height: 1.5rem;
  background: ${CHIP_PENDING_BG};
  color: ${(props: ThemeProp) => props.theme.colors.aiOrange};
  border-radius: 62.5rem;
  padding: 0 0.5rem 0 0.25rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: 1rem;
  white-space: nowrap;
  vertical-align: middle;

  svg {
    stroke: currentColor;
    flex-shrink: 0;
    width: 0.875rem;
    height: 0.875rem;
  }

  .chip-icon {
    display: inline-flex;
    align-items: center;
  }

  &.mention-chip--org {
    background: #ff8fa126;
  }
`;

// =============================================================================
// Sidebar shell
// =============================================================================

interface SidebarWrapperProps {
  $isOpen: boolean;
}

export const SidebarWrapper = styled.div<SidebarWrapperProps>`
  /* Fixed from top of viewport, full 100vh */
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  overflow: hidden;
  border-left: ${(props: ThemeProp & SidebarWrapperProps) =>
    props.$isOpen ? `1px solid ${props.theme.colors.neutrals.c100}` : 'none'};
`;

export const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0.375rem;
  cursor: col-resize;
  z-index: 10;
  background: transparent;
  transition: background-color 0.15s;

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 0.1875rem;
    height: 2.5rem;
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
    border-radius: 0.25rem;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.primary.c050};

    &::after {
      opacity: 1;
    }
  }
`;

interface SidebarInnerProps {
  $width: number;
  $isExpanded?: boolean;
}

export const SidebarInner = styled.div<SidebarInnerProps>`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  min-width: ${(props: SidebarInnerProps) => (props.$width > 0 ? `${props.$width}px` : 'unset')};

  ${({ $isExpanded }) =>
    $isExpanded &&
    css`
      background-image: url(${agentBackground});
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      background-color: ${(props: ThemeProp) => props.theme.colors.background};
    `}
`;

export const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: calc(${headerHeight} + 1px);
  padding: 1rem 1.25rem 0.75rem 1.25rem;
  flex-shrink: 0;

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.mobile}) {
    height: auto;
    padding: 1rem 1.25rem;
  }
`;

export const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
`;

export const SidebarTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
  flex: 1;
`;

export const SidebarTitleIcon = styled.div`
  display: flex;
  align-items: center;

  svg {
    color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const SidebarTitleText = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  line-height: 1;
  font-size: 1.125rem;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

/** Icon-only button to expand or collapse the sidebar */
export const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  line-height: 1;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

/** Icon-only button to edit the conversation title */
export const EditTitleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  line-height: 1;
  transition: background-color 0.15s, color 0.15s;

  & svg {
    stroke: currentColor;
  }

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

/** Icon-only button to create a new conversation */
export const NewConversationIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  height: 1.5rem;
  width: 1.5rem;
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  flex-shrink: 0;
  transition: background-color 0.15s, color 0.15s;

  svg {
    width: 0.5rem;
    height: 0.5rem;
    stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

/** Badge showing total conversation count; clicking opens the popover */
export const ConversationCountBadge = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 2px solid ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  border-radius: 0.375rem;
  padding: 0.0625rem 0.1875rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xxs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;
`;

// =============================================================================
// Conversation popover
// =============================================================================

/** Wrapper that anchors the popover relative to the badge */
export const ConversationsDropdownWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

interface ConversationsPopoverPanelProps {
  $isOpen: boolean;
  $top: number;
  $right: number;
}

/** Outer wrapper for the popover panel */
export const ConversationsPopoverPanel = styled.div<ConversationsPopoverPanelProps>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  width: 19.5rem;
  position: fixed;
  top: ${({ $top }: ConversationsPopoverPanelProps) => `${$top}px`};
  right: ${({ $right }: ConversationsPopoverPanelProps) => `${$right}px`};
  z-index: 9999;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  padding: 0.75rem;
  gap: 0.5rem;
`;

/** Positions the list and gradient overlays relative to each other */
export const ConversationsPopoverListWrapper = styled.div`
  position: relative;
  max-height: calc(5 * 3.5rem);
  overflow: hidden;
`;

/** Scrollable list — height fills the wrapper */
export const ConversationsPopoverList = styled.div`
  overflow-y: auto;
  max-height: calc(5 * 3.5rem);
  display: flex;
  flex-direction: column;

  &::-webkit-scrollbar {
    width: 0.25rem;
  }

  &::-webkit-scrollbar-track {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
    border-radius: 0.25rem;
  }
`;

interface ConversationsPopoverGradientProps {
  $visible: boolean;
  $position: 'top' | 'bottom';
}

/** Fade gradient overlaid on the scrollable list */
export const ConversationsPopoverGradient = styled.div<ConversationsPopoverGradientProps>`
  position: absolute;
  left: 0;
  right: 0;
  height: 1.5rem;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s;
  z-index: 1;

  ${({ $position }) =>
    $position === 'top'
      ? `
    top: 0;
    background: linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,0));
  `
      : `
    bottom: 0;
    background: linear-gradient(to top, rgba(255,255,255,1), rgba(255,255,255,0));
  `}
`;

export const ConversationsPopoverItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  width: 100%;
  border-radius: 0.25rem;
  transition: background 0.15s;

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
  }
`;

export const ConversationsPopoverItemTitle = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

export const ConversationsPopoverItemDate = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
`;

/** Fixed footer inside the popover (not scrolled away) */
export const ConversationsPopoverFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  padding-top: 0.5rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.6875rem;
  font-style: italic;
  line-height: 1.375rem;
`;

// =============================================================================
// Expanded-mode header
// =============================================================================

export const ExpandedHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  flex-shrink: 0;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  border-bottom: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
`;

export const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const ExpandedTitle = styled.span`
  font-family: ${(props: ThemeProp) => props.theme.fonts.secondary};
  font-size: 1.25rem;
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

// =============================================================================
// Chat content
// =============================================================================

interface ChatContainerProps {
  $isExpanded?: boolean;
  $emptyExpanded?: boolean;
}

export const ChatContainer = styled.div<ChatContainerProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: ${({ $isExpanded }: ChatContainerProps) => ($isExpanded ? 'visible' : 'hidden')};
  background-color: ${({ $isExpanded, theme }: ChatContainerProps & ThemeProp) =>
    $isExpanded ? 'transparent' : theme.colors.neutrals.white};

  ${({ $isExpanded }) =>
    $isExpanded &&
    css`
      position: relative;
    `}

  /* Empty expanded state: center the empty block + input slot as a group so the
     input stays mounted in a single tree slot (no remount when isEmpty toggles). */
  ${({ $emptyExpanded }) =>
    $emptyExpanded &&
    css`
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
    `}
`;

export const MessagesListWrapper = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

interface MessagesScrollGradientProps {
  $visible: boolean;
  $position: 'top' | 'bottom';
}

export const MessagesScrollGradient = styled.div<MessagesScrollGradientProps>`
  position: absolute;
  ${({ $position }) => ($position === 'top' ? 'top: 0;' : 'bottom: 0;')}
  left: 0;
  right: 0;
  height: 4rem;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s;
  background: ${({ $position, theme }: ThemeProp & MessagesScrollGradientProps) =>
    $position === 'top'
      ? `linear-gradient(to bottom, ${theme.colors.neutrals.white}, transparent)`
      : `linear-gradient(to top, ${theme.colors.neutrals.white}, transparent)`};
  z-index: 1;
`;

interface MessagesListProps {
  $isExpanded?: boolean;
}

export const MessagesList = styled.div<MessagesListProps>`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 1.25rem;
  padding-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: ${({ $isExpanded, theme }: MessagesListProps & ThemeProp) =>
    $isExpanded ? 'transparent' : theme.colors.neutrals.white};
  min-height: 0;

  ${({ $isExpanded }) =>
    $isExpanded &&
    css`
      padding-left: max(1.25rem, calc((100% - 48rem) / 2));
      padding-right: max(1.25rem, calc((100% - 48rem) / 2));
    `}

  &::-webkit-scrollbar {
    width: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c300};
    border-radius: 0.25rem;

    &:hover {
      background: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
    }
  }
`;

interface MessageContainerProps {
  $isUser?: boolean;
}

export const MessageContainer = styled.div<MessageContainerProps>`
  display: flex;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
  min-width: 0;
`;

export const MessageBubble = styled.div`
  max-width: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 0;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

export const UserMessageBubble = styled(MessageBubble)`
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  border: none;
  border-radius: 0.5rem;
  max-width: 80%;
  width: unset;
  padding: 0.5rem;
`;

export const MarkdownContent = styled.div`
  white-space: normal;
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};

  /* Read-only mention chip (user message bubble) */
  .mention-chip {
    ${mentionChipBaseStyles}
  }

  /* Clickable chip links in chat messages */
  a.mention-chip {
    text-decoration: none;
    cursor: pointer;

    &:hover {
      opacity: 0.85;
      color: ${(props: ThemeProp) => props.theme.colors.aiOrange};
    }
  }

  p {
    margin: 0 0 0.75rem 0;
    font-family: inherit;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-style: normal;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
    letter-spacing: normal;

    &:last-child {
      margin-bottom: 0;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1rem 0 0.5rem 0;
    font-family: inherit;
    font-style: normal;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
    letter-spacing: normal;
    line-height: 1.3;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.ml};
  }

  h2 {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  }

  h3 {
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
    font-family: inherit;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-style: normal;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
    letter-spacing: normal;
  }

  ul {
    font-family: inherit;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  }

  code {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
    padding: 0.125rem 0.375rem;
    border-radius: 0.125rem;
    font-family: inherit;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-style: italic;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
    letter-spacing: normal;
    color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
  }

  pre {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
    padding: 0.75rem 1rem;
    border-radius: 0.375rem;
    overflow-x: auto;
    white-space: pre;
    word-break: normal;
    max-width: 100%;
    margin: 0.75rem 0;

    code {
      background-color: transparent;
      padding: 0;
    }
  }

  blockquote {
    position: relative;
    border-radius: 0.75rem;
    width: 100%;
    border: 1px solid ${(props: ThemeProp) => props.theme.colors.primary.c500};
    background: ${(props: ThemeProp) => props.theme.colors.primary.c050};
    padding: 0.75rem 1rem 0.75rem 2.25rem;
    margin: 0.75rem 0;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    box-sizing: border-box;
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};

    &::before {
      content: '';
      position: absolute;
      left: 1rem;
      top: 50%;
      margin: auto 0;
      width: 0.75rem;
      height: 0.75rem;
      background-image: ${BLOCKQUOTE_INFO_ICON};
      background-size: contain;
      background-repeat: no-repeat;
      transform: translateY(-50%);
    }
  }

  a {
    color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
    text-decoration: underline;

    &:hover {
      color: ${(props: ThemeProp) => props.theme.colors.primary.c600};
    }
  }

  .ai-table-scroll {
    overflow-x: auto;
    width: 100%;
    margin: 0.75rem 0;
    border-radius: 0.5rem;
    border: 1px solid #e9e9eb;
  }

  table {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
  }

  th,
  td {
    text-align: left;
  }

  th {
    background: ${(props: ThemeProp) => props.theme.colors.primary.c050};
    padding: 0.25rem 1rem;
    border-right: 1px solid #e9e9eb;
    border-bottom: 1px solid #e9e9eb;
    font-family: inherit;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-style: normal;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
    letter-spacing: normal;
  }

  th:last-child {
    border-right: none;
  }

  td {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
    padding: 0.25rem 1rem;
    border-right: 1px solid #e9e9eb;
    border-bottom: 1px solid #e9e9eb;
    font-family: inherit;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-style: normal;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
    letter-spacing: normal;
  }

  td:last-child {
    border-right: none;
  }

  tr:last-child td {
    border-bottom: none;
  }

  hr {
    border: none;
    border-top: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c200};
    margin: 1rem 0;
  }

  strong {
    font-family: inherit;
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.semibold};
    letter-spacing: normal;
  }
`;

export const InputContainer = styled.div<{ $isSidebarInCollapsedMode?: boolean }>`
  margin-top: 0.75rem;
  background-color: ${({ theme }: ThemeProp) => theme.colors.neutrals.white};
  border-top-left-radius: 0.75rem;
  border-top-right-radius: 0.75rem;
  flex-shrink: 0;
  padding: ${({ $isSidebarInCollapsedMode }) => ($isSidebarInCollapsedMode ? '0.75rem 1.25rem 0.875rem' : '0')};
`;

interface InputWrapperProps {
  $borderHidden?: boolean;
}

export const InputWrapper = styled.div<InputWrapperProps>`
  display: flex;
  flex-direction: column;
  flex: 1;
  border: 1.5px solid transparent;
  border-radius: 0.75rem;
  background: ${({ $borderHidden, theme }: ThemeProp & InputWrapperProps) =>
    $borderHidden
      ? theme.colors.neutrals.white
      : `linear-gradient(${theme.colors.neutrals.white}, ${theme.colors.neutrals.white}) padding-box, ${theme.gradients.aiInsights} border-box`};
  padding: 0.75rem 0.75rem 0.625rem 1rem;
  gap: 0.625rem;
  position: relative;
`;

export const TextareaWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  min-height: 3rem;
  position: relative;
  overflow: hidden;
`;

export const StyledTextarea = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  border-radius: 0;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  background: transparent;
  resize: none;
  min-height: 3rem;
  padding: 0;
  margin: 0;
  display: block;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  text-align: left;
  direction: ltr;
  scrollbar-width: none;
  max-height: 0;
  transition: max-height 0.15s ease-out;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  }
`;

export const InputActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const AddAudienceButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  gap: 0.125rem;
  background: transparent;
  border-radius: 0.25rem;
  padding: 0.125rem 0.5rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  line-height: 1.3125rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
  }

  svg {
    width: 0.5rem;
    height: 0.5rem;
    flex-shrink: 0;
    stroke-width: 2;
    stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  }
`;

export const ButtonWrapper = styled.div`
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SendButton = styled(Button)`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 50%;
  position: relative;
  padding: 0;
  height: 2rem;
  min-width: 2rem;
  width: 2rem;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.black} !important;
  border: none;
  box-shadow: none;

  svg {
    width: 1rem;
    height: 1rem;
    color: #fff;
    z-index: 3;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c800} !important;
  }

  &:hover:not(:disabled) {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c800};
  }
`;

const dotAnimation = keyframes`
  0%, 80%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  40% {
    opacity: 1;
    transform: scale(1);
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1.25rem;
  background-color: #fff;
  flex: 1;
  min-height: 100%;
  width: 100%;
`;

export const LoadingTitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.l};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.l};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  text-align: center;
`;

export const LoadingSubtitle = styled.div`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  text-align: center;
`;

export const LoadingDotsContainer = styled.div`
  display: flex;
  gap: 0.375rem;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
`;

interface LoadingDotProps {
  $delay: number;
}

export const LoadingDot = styled.div<LoadingDotProps>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${(props: ThemeProp) => props.theme.colors.aiOrange};
  animation: ${dotAnimation} 1.4s infinite;
  animation-delay: ${({ $delay }) => $delay}s;
`;

export const LoadingMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem;
`;

interface EmptyStateContainerProps {
  $compact?: boolean;
}

export const EmptyStateContainer = styled.div<EmptyStateContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${({ $compact }) => ($compact ? '' : 'flex: 1; min-height: 100%;')}
  width: 100%;
  gap: 0.5rem;
  padding: 1.25rem;
`;

export const ExpandedEmptyCenterWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow: hidden;
`;

export const ExpandedEmptyContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 48rem;
`;

export const EmptyStateTitle = styled.div`
  text-align: center;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 2rem;
  background: ${(props: ThemeProp) => props.theme.gradients.primary};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

export const EmptyStateSubtitle = styled.div`
  text-align: center;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  max-width: 16rem;
`;

interface ExpandedContentCenterProps {
  $isExpanded?: boolean;
  $showTopFade?: boolean;
  $emptyExpanded?: boolean;
}

export const ExpandedContentCenter = styled.div<ExpandedContentCenterProps>`
  ${({ $isExpanded, $emptyExpanded }) =>
    $isExpanded &&
    !$emptyExpanded &&
    css`
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 100%;
      max-width: 48rem;
      z-index: 1;
      background-color: ${({ theme }: ThemeProp) => theme.colors.neutrals.white};

      &::before {
        content: '';
        position: absolute;
        top: -3rem;
        left: 0;
        right: 0;
        height: 3rem;
        background: linear-gradient(to bottom, transparent, white);
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s;
      }
    `}

  ${({ $isExpanded, $showTopFade, $emptyExpanded }) =>
    $isExpanded &&
    !$emptyExpanded &&
    $showTopFade &&
    css`
      &::before {
        opacity: 1;
      }
    `}

  /* Empty expanded: in-flow (not docked) so it centers with the empty block. */
  ${({ $emptyExpanded }) =>
    $emptyExpanded &&
    css`
      width: 100%;
      max-width: 48rem;
    `}
`;

export const SuggestedActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  flex-shrink: 0;
`;

export const SuggestedActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  width: 100%;
  border-radius: 0.375rem;
  background-color: #f5f9ff;
  color: ${(props: ThemeProp) => props.theme.colors.primary.c500};
  border: none;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  cursor: pointer;
  transition: background-color 0.15s;
  text-align: left;
  min-width: 0;

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.primary.c050};
  }
`;

export const SuggestedActionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const SuggestedActionIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;

  svg {
    stroke: ${(props: ThemeProp) => props.theme.colors.primary.c500};
    width: 0.875rem;
    height: 0.875rem;
    transform: scaleX(-1);
  }
`;

const toolStatusPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.75);
    opacity: 0.6;
  }
`;

export const ToolStatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
`;

export const ToolStatusDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background-color: ${(props: ThemeProp) => props.theme.colors.aiOrange};
  animation: ${toolStatusPulse} 1.4s ease-in-out infinite;
  flex-shrink: 0;
`;

interface ToolStatusTextProps {
  $visible?: boolean;
}

export const ToolStatusText = styled.span<ToolStatusTextProps>`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.s};
  font-style: italic;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '6px')});
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
`;

export const PrivacyDisclaimer = styled.div`
  text-align: center;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.xs};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  padding: 0.5rem 1.25rem 1rem 1.25rem;
  flex-shrink: 0;
  background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
`;

// =============================================================================
// Content push wrapper — used by OverviewPage to push content left on desktop
// when the sidebar is open (sidebar is position:fixed so it needs manual offset)
// =============================================================================

export const ContentPushWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const StyledBottomSheet = styled(BottomSheet)`
  /* Full-height sheet */
  [data-rsbs-overlay] {
    min-height: calc(100dvh - 3rem);
  }

  /* Let inner MessagesList own scrolling */
  [data-rsbs-scroll] {
    overflow: hidden;
    height: 100%;
  }

  /* Fill full height and remove default padding */
  [data-rsbs-content] {
    padding: 0;
    height: 100%;
  }
`;

// =============================================================================
// Mention editor
// =============================================================================

export const StyledEditorDiv = styled.div`
  flex: 1;
  border: none;
  outline: none;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.m};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  line-height: ${(props: ThemeProp) => props.theme.lineHeight.m};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  background: transparent;
  min-height: 3rem;
  max-height: none;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  word-wrap: break-word;
  white-space: pre-wrap;
  scrollbar-width: none;

  &:empty::before {
    content: attr(data-placeholder);
    color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
    pointer-events: none;
  }

  &:focus {
    outline: none;
  }

  /* Chip inside editor */
  .mention-chip {
    ${mentionChipBaseStyles}
    cursor: default;
    user-select: none;
    padding-right: 0.25rem;

    /* Highlight when chip is covered by a browser selection (toggled via JS) */
    &.mention-chip--selected {
      background: ${(props: ThemeProp) => props.theme.colors.aiOrange}60;
      box-shadow: 0 0 0 2px ${(props: ThemeProp) => props.theme.colors.aiOrange}40;
    }

    .chip-remove {
      display: inline-flex;
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      color: currentColor;
      opacity: 0.7;
      padding: 0;
      font-size: inherit;
      line-height: 1;
      margin-left: 0.0625rem;

      &:hover {
        opacity: 1;
      }
    }
  }

  /* While user is typing a @mention query — orange caret */
  &[data-mention-pending] {
    caret-color: ${(props: ThemeProp) => props.theme.colors.aiOrange};

    .mention-chip-pending {
      color: ${(props: ThemeProp) => props.theme.colors.aiOrange};
    }
  }

  /* In-progress pending chip shown while the user types @query */
  .mention-chip-pending {
    display: inline-flex;
    align-items: center;
    height: 1.5rem;
    position: relative;
    isolation: isolate;
    background: transparent;
    border-radius: 62.5rem;
    padding: 0 0.25rem;
    font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
    font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
    line-height: 1rem;
    white-space: nowrap;
    vertical-align: middle;

    &::selection,
    *::selection {
      background: ${(props: ThemeProp) => props.theme.colors.aiOrange}40;
    }

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      z-index: -1;
      background: ${CHIP_PENDING_BG};
      border-radius: inherit;
      pointer-events: none;
    }
  }
`;

// =============================================================================
// Mention dropdown (inline @ trigger)
// =============================================================================

interface MentionDropdownProps {
  $top: number;
  $left: number;
}

export const MentionDropdown = styled.div<MentionDropdownProps>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  z-index: 9999;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 14rem;
  max-width: 22rem;
  overflow: hidden;
`;

export const MentionDropdownScrollList = styled.div`
  overflow-y: auto;
  max-height: 15.5rem;
  overscroll-behavior: contain;
  padding: 0.25rem 0;
  position: relative;
`;

interface MentionDropdownItemProps {
  $highlighted: boolean;
}

export const MentionDropdownItem = styled.button<MentionDropdownItemProps>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  border: none;
  background: ${({ $highlighted, theme }: ThemeProp & MentionDropdownItemProps) =>
    $highlighted ? theme.colors.neutrals.c050 : 'transparent'};
  cursor: pointer;
  text-align: left;
  padding: 0.5rem 0.75rem;
  transition: background 0.1s;

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
  }
`;

export const MentionDropdownItemIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c500};

  svg {
    width: 1rem;
    height: 1rem;
    stroke: currentColor;
  }
`;

export const MentionDropdownItemName = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const MentionDropdownEmpty = styled.div`
  padding: 0.625rem 0.75rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
  text-align: center;
`;

// =============================================================================
// Add Audience popover
// =============================================================================

interface AudiencePopoverPanelProps {
  $isOpen: boolean;
}

export const AudiencePopoverPanel = styled.div<AudiencePopoverPanelProps>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  position: fixed;
  z-index: 9999;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.05), 0 3px 5px 0 rgba(0, 0, 0, 0.03);
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  border: 1px solid ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  padding: 0.75rem;
  gap: 0;
  overscroll-behavior: contain;
`;

export const AudiencePopoverBackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

export const AudiencePopoverBackLabel = styled.span`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
`;

export const AudiencePopoverBackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.c600};
  flex-shrink: 0;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${(props: ThemeProp) => props.theme.colors.neutrals.c100};
  }

  svg {
    width: 0.75rem;
    height: 0.75rem;
    stroke: currentColor;
    transform: rotate(90deg);
  }
`;

export const AudiencePopoverInput = styled(Input)`
  margin: 0;
  margin-bottom: 0 !important;

  & .nextui-input-container {
    height: 2.5rem !important;
  }
`;

interface AudiencePopoverListSectionProps {
  $fixed?: boolean;
}

export const AudiencePopoverListSection = styled.div<AudiencePopoverListSectionProps>`
  position: relative;
  overflow: hidden;
  ${({ $fixed }) => $fixed && 'height: 13rem;'}

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    height: 100%;
  }
`;

export const AudiencePopoverScrollList = styled.div<AudiencePopoverListSectionProps>`
  overflow-y: auto;
  height: 100%;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  ${({ $fixed }) => $fixed && 'max-height: 13rem;'}

  @media (max-width: ${(props: ThemeProp) => props.theme.breakpoints.tablet}) {
    max-height: unset;
  }
`;

export const AudiencePopoverShimmerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
`;

export const StyledSearchIcon = styled(SearchIcon)`
  stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.c400};
`;

export const AudienceTypeListItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  padding: 0.5rem 0.625rem;
  border-radius: 0.375rem;
  font-size: ${(props: ThemeProp) => props.theme.fontSize.s};
  font-family: ${(props: ThemeProp) => props.theme.fonts.primary};
  color: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
  transition: background 0.1s;

  &:hover {
    background: ${(props: ThemeProp) => props.theme.colors.neutrals.c050};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
    stroke: ${(props: ThemeProp) => props.theme.colors.neutrals.black};
    flex-shrink: 0;
  }
`;

export const AudienceMobileBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;
`;

export const MobileBottomSheetContent = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
`;

export const AudienceMobileOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  background: ${(props: ThemeProp) => props.theme.colors.neutrals.white};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
